import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import {
  ButtonModule,
  CheckBoxModule,
  RadioButtonModule,
} from '@syncfusion/ej2-angular-buttons';
import {
  DatePickerModule,
  DateTimePickerModule,
} from '@syncfusion/ej2-angular-calendars';
import {
  DropDownListAllModule,
  DropDownListModule,
  MultiSelectAllModule,
} from '@syncfusion/ej2-angular-dropdowns';
import {
  DetailRowService,
  EditService,
  GridModule,
  PagerModule,
  PageService,
  PdfExportService,
  ReorderService,
  ResizeService,
  SearchService,
  SortService,
  ToolbarService,
} from '@syncfusion/ej2-angular-grids';
import {
  MaskedTextBoxModule,
  NumericTextBoxAllModule,
  NumericTextBoxModule,
  TextBoxModule,
  UploaderModule,
} from '@syncfusion/ej2-angular-inputs';
import {
  CarouselModule,
  ContextMenuModule,
  SidebarModule,
  TabModule,
  ToolbarModule,
  TreeViewModule,
} from '@syncfusion/ej2-angular-navigations';
import { DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import {
  DropDownButtonModule,
  ProgressButtonModule,
} from '@syncfusion/ej2-angular-splitbuttons';

import { HorizonComponentsCoreModule } from '@horizon/core';
import {
  HorizonButtonModule,
  HorizonConfirmationButtonsSetModule,
} from '@horizon/core-buttons';
import {
  HorizonDropdownModule,
  HorizonMultiselectModule,
} from '@horizon/core-dropdowns';
import { HorizonGenericGridModule } from '@horizon/core-grids';
import { HorizonDocIconModule, HorizonIconModule } from '@horizon/core-icons';
import {
  HorizonCheckboxInputModule,
  HorizonDatepickerModule,
  HorizonNumberInputModule,
  HorizonSwitchInputModule,
  HorizonTextareaModule,
  HorizonTextInputModule,
} from '@horizon/core-inputs';
import {
  HorizonSectionHeaderModule,
  HorizonSectionSubHeaderModule,
  HorizonTooltipModule,
} from '@horizon/core-layouts';
import { HorizonNotificationMsgModule } from '@horizon/core-notifications';
import { HorizonNotesActivitiesViewModule } from '@horizon/core-pages';
import { HorizonComponentsDmCoreModule } from '@horizon/dm-core';
import {
  HorizonDynamicEditModalModule,
  HorizonDynamicFormModule,
} from '@horizon/dm-forms';
import { NewHorizonDynamicGridModule } from '@horizon/dm-grids';
import {
  AccumulationChartAllModule,
  BarSeriesService,
  CategoryService,
  ChartAllModule,
  ChartAnnotationService,
  LineSeriesService,
  MultiLevelLabelService,
  RangeColumnSeriesService,
  StackingBarSeriesService,
} from '@syncfusion/ej2-angular-charts';
import {
  CircularGaugeAllModule,
  GaugeTooltipService,
} from '@syncfusion/ej2-angular-circulargauge';
import { ProgressBarModule } from '@syncfusion/ej2-angular-progressbar';
import {
  HtmlEditorService,
  ImageService,
  LinkService,
  RichTextEditorModule,
  ToolbarService as ToolbarServiceRte,
} from '@syncfusion/ej2-angular-richtexteditor';
import { AccountAllCustomersComponent } from './components/account-all-customers/account-all-customers.component';
import { AddCustomerModalComponent } from './components/account-all-customers/add-customer-modal/add-customer-modal.component';
import { ChatFloatingScreenComponent } from './components/chat-floating-screen/chat-floating-screen.component';
import { CommunicationCardComponent } from './components/communication-card/communication-card.component';
import { CustomersCommunicationComponent } from './components/customers-communication/customers-communication.component';
import { EditModalCommunicationComponent } from './components/customers-communication/edit-modal-communication/edit-modal-communication.component';
import { DataGridComponent } from './components/data-grid/data-grid.component';
import { FormatCellElementPipe } from './components/data-grid/format-cell-element.pipe';
import { DataTableComponent } from './components/data-table/data-table.component';
import { GetCellIconPipe } from './components/data-table/pipes/get-cell-icon.pipe';
import { DateCardComponent } from './components/date-cards-chart/date-card/date-card.component';
import { DateCardsChartComponent } from './components/date-cards-chart/date-cards-chart.component';
import { FloatingBoxComponent } from './components/floating-box/floating-box.component';
import { FormCardWithStatusComponent } from './components/form-card-with-status/form-card-with-status.component';
import { GeniousChatComponent } from './components/genious-chat/genious-chat.component';
import { AccordionSectionComponent } from './components/horizon-genesys-call-log/components/accordion-section/accordion-section.component';
import { HorizonOtpInputComponent } from './components/horizon-genesys-call-log/components/horizon-otp-input/horizon-otp-input.component';
import { FormatNumberToOrdinalPipe } from './components/horizon-genesys-call-log/components/horizon-otp-input/pipes/format-number-to-ordinal.pipe';
import { NotificationListComponent } from './components/horizon-genesys-call-log/components/notification-list/notification-list.component';
import { RadioValidationLabelComponent } from './components/horizon-genesys-call-log/components/radio-validation-label/radio-validation-label.component';
import { VerificationLabelComponent } from './components/horizon-genesys-call-log/components/verification-label/verification-label.component';
import { HorizonGenesysCallLogComponent } from './components/horizon-genesys-call-log/horizon-genesys-call-log.component';
import { FormsService } from './components/horizon-genesys-call-log/services/forms.service';
import { CallLogReportViewComponent } from './components/horizon-genesys-call-log/views/call-log-report-view/call-log-report-view.component';
import { CallSearchViewComponent } from './components/horizon-genesys-call-log/views/call-log-search-view/call-log-search-view.component';
import { HorizonGenesysTelephonyComponent } from './components/horizon-genesys-telephony/horizon-genesys-telephony.component';
import { HorizonGenesysTemporalSubstituteComponent } from './components/horizon-genesys-temporal-substitute/horizon-genesys-temporal-substitute.component';
import { HorizonPaymentGetawayComponent } from './components/horizon-payment-getaway/horizon-payment-getaway.component';
import { KeyInfoCardComponent } from './components/key-info-cards/key-info-card/key-info-card.component';
import { KeyInfoCardsComponent } from './components/key-info-cards/key-info-cards.component';
import { KycComponent } from './components/kyc/kyc.component';
import { ListItemWithStatusComponent } from './components/list-item-with-status/list-item-with-status.component';
import { PdfPreviewModalComponent } from './components/pdf-preview-modal/pdf-preview-modal.component';
import { SendRequestModalComponent } from './components/send-request-modal/send-request-modal.component';
import { SplineLinesChartComponent } from './components/spline-lines-chart/spline-lines-chart.component';
import { VulnerabilityModalComponent } from './components/vulnerability/vulnerability-modal/vulnerability-modal.component';
import { VulnerabilityComponent } from './components/vulnerability/vulnerability.component';
import { CheckChartPermissionPipe } from './pipes/check-chart-permission.pipe';
import { DisableReportPaymentButtonPipe } from './pipes/disable-report-payment-button.pipe';
import { DisableReportSaveButtonPipe } from './pipes/disable-report-save-button.pipe';
import { ExistsChartDataPipe } from './pipes/exists-chart-data.pipe';
import { GetChartColumnsPipe } from './pipes/get-chart-columns.pipe';
import { GetChartDataPipe } from './pipes/get-chart-data.pipe';
import { GetInnerGridModalContextPipe } from './pipes/get-inner-grid-modal-context.pipe';
import { GetInnerGridModalTitlePipe } from './pipes/get-inner-grid-modal-title.pipe';
import { MapAccountValuePipe } from './pipes/map-account-value.pipe';

@NgModule({
  declarations: [
    FloatingBoxComponent,
    CommunicationCardComponent,
    CustomersCommunicationComponent,
    EditModalCommunicationComponent,
    KycComponent,
    ListItemWithStatusComponent,
    FormCardWithStatusComponent,
    SendRequestModalComponent,
    PdfPreviewModalComponent,
    VulnerabilityComponent,
    VulnerabilityModalComponent,
    MapAccountValuePipe,
    KeyInfoCardsComponent,
    KeyInfoCardComponent,
    SplineLinesChartComponent,
    DataGridComponent,
    FormatCellElementPipe,
    DateCardsChartComponent,
    DateCardComponent,
    HorizonGenesysTelephonyComponent,
    DataTableComponent,
    GeniousChatComponent,
    ChatFloatingScreenComponent,
    GetCellIconPipe,
    GetInnerGridModalTitlePipe,
    GetInnerGridModalContextPipe,
    DisableReportSaveButtonPipe,
    DisableReportPaymentButtonPipe,
    HorizonGenesysCallLogComponent,
    CallLogReportViewComponent,
    CallSearchViewComponent,
    VerificationLabelComponent,
    AccordionSectionComponent,
    RadioValidationLabelComponent,
    HorizonGenesysTemporalSubstituteComponent,
    HorizonPaymentGetawayComponent,
    AccountAllCustomersComponent,
    AddCustomerModalComponent,
    HorizonOtpInputComponent,
    FormatNumberToOrdinalPipe,
    NotificationListComponent,
    ExistsChartDataPipe,
    GetChartDataPipe,
    GetChartColumnsPipe,
    CheckChartPermissionPipe,
  ],
  imports: [
    TranslateModule,
    CarouselModule,
    CommonModule,
    ReactiveFormsModule,
    GridModule,
    PagerModule,
    ButtonModule,
    RadioButtonModule,
    TabModule,
    ToolbarModule,
    SidebarModule,
    TreeViewModule,
    CheckBoxModule,
    MultiSelectAllModule,
    NumericTextBoxModule,
    NumericTextBoxAllModule,
    DialogModule,
    TooltipModule,
    DatePickerModule,
    DateTimePickerModule,
    DropDownListAllModule,
    FormsModule,
    MaskedTextBoxModule,
    DropDownListModule,
    UploaderModule,
    TextBoxModule,
    ProgressButtonModule,
    DropDownButtonModule,
    RichTextEditorModule,
    ChartAllModule,
    CircularGaugeAllModule,
    ContextMenuModule,
    ProgressBarModule,
    AccumulationChartAllModule,
    //Horizon Components Library
    HorizonIconModule,
    HorizonSectionSubHeaderModule,
    HorizonComponentsCoreModule,
    HorizonComponentsDmCoreModule,
    HorizonDropdownModule,
    HorizonDocIconModule,
    HorizonSectionHeaderModule,
    HorizonDynamicEditModalModule,
    HorizonDynamicFormModule,
    HorizonConfirmationButtonsSetModule,
    HorizonTextInputModule,
    HorizonDatepickerModule,
    HorizonTextareaModule,
    HorizonSwitchInputModule,
    HorizonGenericGridModule,
    HorizonNotificationMsgModule,
    HorizonButtonModule,
    HorizonMultiselectModule,
    HorizonCheckboxInputModule,
    NewHorizonDynamicGridModule,
    HorizonNumberInputModule,
    HorizonTooltipModule,
    HorizonNotesActivitiesViewModule,
  ],
  providers: [
    BarSeriesService,
    StackingBarSeriesService,
    CategoryService,
    PageService,
    SortService,
    PdfExportService,
    EditService,
    ToolbarService,
    SearchService,
    ReorderService,
    ResizeService,
    DetailRowService,
    TranslatePipe,
    ToolbarServiceRte,
    LinkService,
    ImageService,
    HtmlEditorService,
    GaugeTooltipService,
    DatePipe,
    FormatCellElementPipe,
    MultiLevelLabelService,
    LineSeriesService,
    ChartAnnotationService,
    RangeColumnSeriesService,
    GetInnerGridModalTitlePipe,
    GetInnerGridModalContextPipe,
    DisableReportSaveButtonPipe,
    DisableReportPaymentButtonPipe,
    FormsService,
    CurrencyPipe,
  ],
  exports: [
    FloatingBoxComponent,
    CommunicationCardComponent,
    CustomersCommunicationComponent,
    EditModalCommunicationComponent,
    KycComponent,
    ListItemWithStatusComponent,
    FormCardWithStatusComponent,
    SendRequestModalComponent,
    PdfPreviewModalComponent,
    VulnerabilityComponent,
    VulnerabilityModalComponent,
    KeyInfoCardsComponent,
    KeyInfoCardComponent,
    SplineLinesChartComponent,
    DataGridComponent,
    DateCardsChartComponent,
    HorizonGenesysTelephonyComponent,
    DataTableComponent,
    GeniousChatComponent,
    ChatFloatingScreenComponent,
    GetInnerGridModalTitlePipe,
    GetInnerGridModalContextPipe,
    DisableReportSaveButtonPipe,
    DisableReportPaymentButtonPipe,
    HorizonGenesysCallLogComponent,
    CallLogReportViewComponent,
    CallSearchViewComponent,
    VerificationLabelComponent,
    AccordionSectionComponent,
    HorizonGenesysTemporalSubstituteComponent,
    AccountAllCustomersComponent,
    AddCustomerModalComponent,
    HorizonOtpInputComponent,
    ExistsChartDataPipe,
    GetChartDataPipe,
    GetChartColumnsPipe,
    CheckChartPermissionPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
